// Standard timer zones for CS:S maps from zones-cstrike (https://github.com/ddyfad/zones-cstrike):
// the zones bhop servers use with shavit's bhoptimer. We ship a packed copy
// (public/zones/zones-cstrike.json, made by scripts/update-zones.mjs) that loads the first time a
// map is played; maps added since then come from the zones-cstrike site, one file per map.
// Used when a loaded map has no zones of its own yet; the game works the same without it.

import type { Zone } from '../sim/zones';
import type { MapZones } from '../storage/zoneFile';

export const STANDARD_ZONES_NAME = 'zones-cstrike';
const BUNDLED_URL = import.meta.env.BASE_URL + 'zones/zones-cstrike.json';
const LIVE_URL = 'https://zones-cstrike.srcwr.com/z/';

/** Corners as [ax, ay, az, bx, by, bz] (Source units, any order). */
type Box = number[];
/** Packed file: map name (lowercase) -> [start, end, tier]. */
interface Bundle {
  maps: Record<string, [Box | null, Box | null, number]>;
}

/** One entry of a zones-cstrike map file. */
interface RawZone {
  point_a?: unknown;
  point_b?: unknown;
  track?: unknown;
  type?: unknown;
}

/** Player hull: 16 units each side of the origin, 72 tall (standing). */
const HALF_WIDTH = 16;
const HULL_HEIGHT = 72;

/**
 * A zones-cstrike box as one of our zones. On the server a zone is a trigger the player's hull
 * touches; ours test the origin (at the feet), so the box grows by the hull to start and stop the
 * timer at the same moment.
 */
function toZone(b: unknown): Zone | null {
  if (!Array.isArray(b) || b.length !== 6 || !b.every((n) => typeof n === 'number' && Number.isFinite(n)))
    return null;
  const [ax, ay, az, bx, by, bz] = b as number[];
  return {
    min: [Math.min(ax, bx) - HALF_WIDTH, Math.min(ay, by) - HALF_WIDTH, Math.min(az, bz) - HULL_HEIGHT],
    max: [Math.max(ax, bx) + HALF_WIDTH, Math.max(ay, by) + HALF_WIDTH, Math.max(az, bz)],
  };
}

function zones(map: string, start: Zone | null, end: Zone | null): MapZones | null {
  return start || end ? { map, start, end, checkpoints: [], source: STANDARD_ZONES_NAME } : null;
}

/** The main track's start and end from a zones-cstrike map file, or null if it has neither. */
export function parseStandardZones(map: string, raw: unknown): MapZones | null {
  if (!Array.isArray(raw)) return null;
  const main = (raw as RawZone[]).filter((z) => z && (z.track ?? 0) === 0);
  const find = (type: string) => {
    const z = main.find((x) => x.type === type);
    return z && Array.isArray(z.point_a) && Array.isArray(z.point_b)
      ? toZone([...z.point_a, ...z.point_b])
      : null;
  };
  return zones(map, find('start'), find('end'));
}

/** A map's zones from the packed file, or null if it isn't in there. */
export function bundledZones(map: string, bundle: Bundle): MapZones | null {
  const e = bundle.maps[map.toLowerCase()];
  return e ? zones(map, toZone(e[0]), toZone(e[1])) : null;
}

async function getJson(url: string, ms: number): Promise<unknown> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

let bundle: Promise<Bundle | null> | null = null;
/** The packed file, loaded once (a failed load is tried again next time). */
function loadBundle(): Promise<Bundle | null> {
  bundle ??= getJson(BUNDLED_URL, 15000).then((b) => {
    const ok = b && typeof b === 'object' && 'maps' in b ? (b as Bundle) : null;
    if (!ok) bundle = null;
    return ok;
  });
  return bundle;
}

/**
 * The standard zones for a map name: from our packed copy, else (a newer map) from the
 * zones-cstrike site. Null when there are none or nothing can be reached.
 */
export async function fetchStandardZones(map: string): Promise<MapZones | null> {
  const b = await loadBundle();
  if (b && map.toLowerCase() in b.maps) return bundledZones(map, b);
  return parseStandardZones(
    map,
    await getJson(LIVE_URL + encodeURIComponent(map.toLowerCase()) + '.json', 8000),
  );
}
